import { useState, useRef } from "react";
import axios from "axios";
import Navbar from '../navbar';
import TopNavbar from '../topnavbar';
import Footer from '../footer';
import '../../App.css';

const LOAN_TYPES = ["SALARY LOAN", "CALAMITY LOAN"];

const MONTHS = [
    "January", "February", "March",     "April",   "May",      "June",
    "July",    "August",   "September", "October", "November", "December",
];

const TYPE_COLORS = {
    SSS:  { bg: "#1cc88a22", color: "#1cc88a", border: "#1cc88a" },
    HDMF: { bg: "#4e73df22", color: "#4e73df", border: "#4e73df" },
};

const STATUS_COLORS = {
    ACTIVE: {
        active: { background: "#1cc88a", color: "#fff", borderColor: "#1cc88a" },
        idle:   { background: "#fff",    color: "#1cc88a", borderColor: "#1cc88a" },
    },
    SEPARATED: {
        active: { background: "#e74a3b", color: "#fff", borderColor: "#e74a3b" },
        idle:   { background: "#fff",    color: "#e74a3b", borderColor: "#e74a3b" },
    },
};

const LABEL_STYLE = {
    fontSize:        "11px",
    fontWeight:      "700",
    color:           "#5a5c69",
    textTransform:   "uppercase",
    letterSpacing:   "0.5px",
    marginBottom:    "4px",
    display:         "block",
};

const INPUT_STYLE = {
    width:           "100%",
    padding:         "8px 10px",
    border:          "1px solid #d1d3e2",
    borderRadius:    "5px",
    fontSize:        "13px",
    color:           "#3a3b45",
    outline:         "none",
    boxSizing:       "border-box",
    fontFamily:      "Arial",
    backgroundColor: "#fff",
};

const INPUT_READONLY_STYLE = {
    ...INPUT_STYLE,
    backgroundColor: "#f8f9fc",
    color:           "#6e707e",
    cursor:          "not-allowed",
    border:          "1px solid #e3e6f0",
};

const INPUT_ERROR_STYLE = {
    ...INPUT_STYLE,
    border:     "1px solid #e74a3b",
    boxShadow:  "0 0 0 2px rgba(231,74,59,0.2)",
};

const STATUS_BTN_BASE = {
    padding:        "7px 18px",
    borderRadius:   "4px",
    fontSize:       "11px",
    fontWeight:     "700",
    cursor:         "pointer",
    border:         "2px solid transparent",
    transition:     "all 0.15s",
    letterSpacing:  "0.3px",
};

const emptyRow = () => ({ month: "", sbr: "", datePaid: "", loanType: "", amount: "" });

function formatDate(dateStr) {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    if (isNaN(d)) return dateStr;
    return d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
}

function formatPaymentDate(dateStr) {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    if (isNaN(d)) return dateStr;
    const day   = d.getDate();
    const month = d.toLocaleString("en-US", { month: "short" });
    const year  = String(d.getFullYear()).slice(-2);
    return `${day}-${month}-${year}`;
}

function normalizeLoanType(loanType) {
    if (!loanType) return "";
    const lt = loanType.trim().toUpperCase();
    if (lt === "SALARY"   || lt === "SALARY LOAN")   return "SALARY LOAN";
    if (lt === "CALAMITY" || lt === "CALAMITY LOAN") return "CALAMITY LOAN";
    return "";
}

function facilityToCity(facility) {
    if (!facility) return "Philippines";
    const f = facility.trim().toLowerCase();
    if (f.includes("legazpi")) return "Legazpi City, Philippines";
    if (f.includes("manila"))  return "Manila, Philippines";
    if (f.includes("mandaue")) return "Mandaue City, Philippines";
    return `${facility.trim()}, Philippines`;
}

function getTodayIssuance() {
    const now      = new Date();
    const day      = now.getDate();
    const suffixes = ["th", "st", "nd", "rd"];
    const suffix   = (day % 100 >= 11 && day % 100 <= 13)
        ? "th"
        : suffixes[day % 10] || "th";
    const months   = ["January","February","March","April","May","June",
                      "July","August","September","October","November","December"];
    return {
        issuedDay:   `${day}${suffix}`,
        issuedMonth: months[now.getMonth()],
        issuedYear:  now.getFullYear().toString(),
    };
}

function govTypeToAgency(govType) {
    return govType === "SSS" ? "SSS" : "Pagibig";
}

function buildPaymentRowsFromRange(paymentStartStr, paymentEndStr, dbRecords = []) {
    if (!paymentStartStr) return [emptyRow()];
    const start = new Date(paymentStartStr);
    if (isNaN(start)) return [emptyRow()];
    const end = paymentEndStr ? new Date(paymentEndStr) : new Date();
    if (isNaN(end)) return [emptyRow()];

    const recordMap = {};
    for (const rec of dbRecords) {
        if (rec.PaidMonth && rec.PaidYear) {
            const key = `${rec.PaidMonth} ${rec.PaidYear}`.toUpperCase();
            recordMap[key] = rec;
        }
    }

    const rows = [];
    let cur = new Date(start.getFullYear(), start.getMonth(), 1);
    const endBound = new Date(end.getFullYear(), end.getMonth(), 1);

    while (cur <= endBound && rows.length < 60) {
        const mo  = cur.toLocaleString("en-US", { month: "long" }).toUpperCase();
        const yr  = cur.getFullYear();
        const key = `${mo} ${yr}`;
        const rec = recordMap[key];

        rows.push({
            month:    key,
            sbr:      rec?.SBRNumber   ?? "",
            datePaid: rec?.PaymentDate ? formatPaymentDate(rec.PaymentDate) : "",
            loanType: rec?.LoanType    ? normalizeLoanType(rec.LoanType) : "",
            amount:   rec?.PaidAmount != null ? String(rec.PaidAmount) : "",
        });

        cur.setMonth(cur.getMonth() + 1);
    }

    return rows.length > 0 ? rows : [emptyRow()];
}

async function generateCertificateDocx(data, govType, employeeStatus) {
    const { payments, ...form } = data;
    const response = await axios.post("/api/generateCertificate", { form, payments, govType, employeeStatus });
    return response.data.base64;
}

// Main Component ─────────────────────────────────────────────────────────────────────────────
export default function Certificate() {
    const userFacility  = sessionStorage.getItem("facility") || "";
    const todayIssuance = getTodayIssuance();

    const [govType,        setGovType]        = useState("");
    const [loanType,       setLoanType]       = useState("");
    const [employeeStatus, setEmployeeStatus] = useState("");
    const [generating,     setGenerating]     = useState(false);
    const [toast,          setToast]          = useState(null);

    const [form, setForm] = useState({
        governmentNumber: "",
        employeeName:     "",
        companyName:      "",
        startDate:        "",
        endDate:          "",
        paymentStartDate: "",
        paymentEndDate:   "",
        issuedDay:        todayIssuance.issuedDay,
        issuedMonth:      todayIssuance.issuedMonth,
        issuedYear:       todayIssuance.issuedYear,
        issuedCity:       facilityToCity(userFacility),
    });

    const [errorFields, setErrorFields] = useState({});

    const [payments,          setPayments]          = useState([emptyRow()]);
    const [lookupState,       setLookupState]       = useState("idle");
    const [lookupMsg,         setLookupMsg]         = useState("");
    const [noRecordsWarning,  setNoRecordsWarning]  = useState(false);
    const [resolvedEmployeeId, setResolvedEmployeeId] = useState(null);
    const [dbRecords,          setDbRecords]          = useState([]);

    const debounceRef = useRef(null);
    const typeColor = govType ? TYPE_COLORS[govType] : null;

    const showToast = (msg, type = "success") => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3500);
    };

    const set = (key, val) => {
        setForm(f => ({ ...f, [key]: val }));
        if (errorFields[key]) setErrorFields(e => ({ ...e, [key]: false }));
    };

    const fieldStyle = (key) => errorFields[key] ? INPUT_ERROR_STYLE : INPUT_STYLE;

    const resetLookup = () => {
        setPayments([emptyRow()]);
        setLookupState("idle");
        setLookupMsg("");
        setResolvedEmployeeId(null);
        setDbRecords([]);
        setNoRecordsWarning(false);
    };

    const handleTypeChange = (type) => {
        setGovType(type);
        setLoanType("");
        setForm(f => ({
            ...f,
            governmentNumber: "", employeeName: "", companyName: "",
            startDate: "", paymentStartDate: "", paymentEndDate: "",
        }));
        resetLookup();
        setErrorFields({});
    };

    const handleStatusChange = (status) => {
        setEmployeeStatus(prev => prev === status ? "" : status);
        if (errorFields["employeeStatus"]) setErrorFields(e => ({ ...e, employeeStatus: false }));
    };

    const handleLoanTypeChange = (val) => {
        setLoanType(val);
        resetLookup();
        if (form.governmentNumber && govType) {
            lookupEmployee(form.governmentNumber, govType, val);
        }
    };

    const handleGovNumberChange = (val) => {
        setForm(f => ({
            ...f,
            governmentNumber: val,
            employeeName: "", companyName: "",
            startDate: "", paymentStartDate: "", paymentEndDate: "",
        }));
        resetLookup();
        if (errorFields["governmentNumber"]) setErrorFields(e => ({ ...e, governmentNumber: false }));

        if (!govType)   { setLookupMsg("Please select the ID type first.");   return; }
        if (!loanType)  { setLookupMsg("Please select the loan type first."); return; }

        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            if (val.trim().length >= 6) lookupEmployee(val.trim(), govType, loanType);
        }, 600);
    };

    const handlePaymentPeriodChange = (key, val) => {
        set(key, val);
        const updatedStart = key === "paymentStartDate" ? val : form.paymentStartDate;
        const updatedEnd   = key === "paymentEndDate"   ? val : form.paymentEndDate;
        if (updatedStart) {
            setPayments(buildPaymentRowsFromRange(updatedStart, updatedEnd, dbRecords));
        }
    };

    const setPayment = (i, key, val) =>
        setPayments(rows => rows.map((r, idx) => idx === i ? { ...r, [key]: val } : r));

    const addRow = () => setPayments(r => [...r, emptyRow()]);

    const removeRow = (i) => {
        if (payments.length === 1) { setPayments([emptyRow()]); return; }
        setPayments(r => r.filter((_, idx) => idx !== i));
    };

    const lookupEmployee = async (govNum, type, selectedLoanType = loanType) => {
        if (!selectedLoanType) { setLookupMsg("Please select a loan type first."); return; }

        setLookupState("loading");
        setLookupMsg("Looking up employee record...");
        setNoRecordsWarning(false);

        try {
            const res = await axios.get("/api/employeeByGovNumber", {
                params: { govType: type, govNumber: govNum, facility: userFacility },
            });

            const { EmployeeId, EmployeeName, Facility, DateHired } = res.data;

            if (Facility && userFacility && Facility.trim() !== userFacility.trim()) {
                setLookupState("error");
                setLookupMsg("Access denied. This employee belongs to a different branch.");
                return;
            }

            const branch    = Facility ? `${Facility.trim()} Branch` : "";
            const company   = `Innodata Knowledge Services, Inc.${branch ? ", " + branch : ""}`;
            const startDate = formatDate(DateHired);

            setResolvedEmployeeId(EmployeeId);
            setLookupMsg("Fetching payment records...");

            const paymentsRes = await axios.get("/api/loanpayments/byemployee", {
                params: { employeeId: EmployeeId, agency: govTypeToAgency(type), loanType: selectedLoanType },
            });

            const { records, paymentPeriodStart, paymentPeriodEnd } = paymentsRes.data;
            console.log("records from DB:", JSON.stringify(records));
            setDbRecords(records);

            if (records.length > 0) {
                setPayments(buildPaymentRowsFromRange(paymentPeriodStart, paymentPeriodEnd, records));
                setForm(f => ({
                    ...f,
                    employeeName:     EmployeeName,
                    companyName:      company,
                    startDate,
                    paymentStartDate: paymentPeriodStart,
                    paymentEndDate:   paymentPeriodEnd,
                }));
                setLookupState("found");
                setLookupMsg("Employee found");
                setNoRecordsWarning(false);
            } else {
                setForm(f => ({ ...f, employeeName: EmployeeName, companyName: company, startDate }));
                setPayments([emptyRow()]);
                setLookupState("found");
                setLookupMsg("Employee found");
                setNoRecordsWarning(true);
            }
        } catch (err) {
            const status = err?.response?.status;
            const msg = status === 404 ? "No employee found"
                      : status === 400 ? "Invalid request. Check if its SSS or HDMF."
                      : `Lookup failed: ${err?.response?.data?.error || err.message}`;
            setLookupState("error");
            setLookupMsg(msg);
        }
    };

    const handleGenerate = async () => {
        if (!govType) { showToast("Please select the government ID type (SSS or HDMF).", "error"); return; }

        const required = [
            "governmentNumber", "employeeName", "companyName", "startDate",
            "paymentStartDate", "paymentEndDate", "issuedDay", "issuedMonth", "issuedYear",
            ...(employeeStatus === "SEPARATED" ? ["endDate"] : []),
        ];

        const newErrors = {};
        required.forEach(k => { if (!form[k]?.trim()) newErrors[k] = true; });

        if (Object.keys(newErrors).length > 0) {
            setErrorFields(newErrors);
            showToast("Please fill in all required fields.", "error");
            return;
        }

        if (payments.some(p => !p.month)) {
            showToast("Please fill in the Month/Year for all payment rows.", "error");
            return;
        }

        setErrorFields({});
        setGenerating(true);

        try {
            const base64 = await generateCertificateDocx({ ...form, payments }, govType, employeeStatus);
            if (!base64) throw new Error("Document generation returned empty.");

            const bytes = atob(base64);
            const arr   = new Uint8Array(bytes.length);
            for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);

            const blob = new Blob([arr], { type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document" });
            const url  = URL.createObjectURL(blob);
            const a    = document.createElement("a");
            a.href     = url;
            a.download = `CertificateLoanRemittances_${govType}_${form.employeeName.replace(/\s+/g, "_")}.docx`;
            a.click();
            URL.revokeObjectURL(url);

            showToast(`${govType} Certificate generated and downloaded!`);
        } catch (e) {
            showToast(e.message || "Generation failed.", "error");
        } finally {
            setGenerating(false);
        }
    };

    return (
        <div id="wrapper">
            <Navbar />
            <div id="content-wrapper" className="d-flex flex-column">
                <div id="content">
                    <TopNavbar />
                    <div className="container-fluid">

                        {/* ── Toast Notification ── */}
                        {toast && (
                            <div style={{
                                position:        "fixed",
                                top:             20,
                                right:           20,
                                zIndex:          9999,
                                backgroundColor: toast.type === "success" ? "#1cc88a" : "#e74a3b",
                                color:           "#fff",
                                padding:         "12px 20px",
                                borderRadius:    "6px",
                                fontSize:        "13px",
                                fontWeight:      "600",
                                boxShadow:       "0 4px 16px rgba(0,0,0,0.15)",
                                display:         "flex",
                                alignItems:      "center",
                                gap:             8,
                            }}>
                                <i className={`fas ${toast.type === "success" ? "fa-check-circle" : "fa-exclamation-circle"}`} />
                                {toast.msg}
                            </div>
                        )}

                        {/* ── Page Title ── */}
                        <div className="d-flex align-items-center justify-content-center mb-3">
                            <h4 className="m-0 font-weight-bold text-primary header-name">
                                Certificate of Loan Remittances
                            </h4>
                        </div>

                        {/* ── Card 1: Government ID Lookup ── */}
                        <div className="card shadow mb-4">
                            <div className="card-header py-3" style={{ backgroundColor: "#fff", borderBottom: "1px solid #e3e6f0" }}>
                                <h6 className="m-0 font-weight-bold text-primary">
                                    <i className="fas fa-id-card mr-2"></i>Government ID Lookup
                                </h6>
                            </div>
                            <div className="card-body">

                                {/* Employee Status */}
                                <div style={{ marginBottom: 20 }}>
                                    <label style={{ ...LABEL_STYLE, color: errorFields["employeeStatus"] ? "#e74a3b" : "#5a5c69" }}>
                                        Employee Status *
                                    </label>
                                    <div style={{ display: "flex", gap: 8, alignItems: "center", height: 36 }}>
                                        {["ACTIVE", "SEPARATED"].map(status => {
                                            const isSelected = employeeStatus === status;
                                            const colors     = STATUS_COLORS[status];
                                            return (
                                                <button
                                                    key={status}
                                                    type="button"
                                                    onClick={() => handleStatusChange(status)}
                                                    style={{
                                                        ...STATUS_BTN_BASE,
                                                        ...(isSelected ? colors.active : colors.idle),
                                                        boxShadow: errorFields["employeeStatus"] && !isSelected
                                                            ? "0 0 0 2px rgba(231,74,59,0.2)"
                                                            : "none",
                                                    }}
                                                >
                                                    <i className={`fas ${status === "ACTIVE" ? "fa-user-check" : "fa-user-times"} mr-1`} />
                                                    {status === "ACTIVE" ? "Active" : "Separated"}
                                                </button>
                                            );
                                        })}
                                    </div>
                                    {errorFields["employeeStatus"] && (
                                        <div style={{ fontSize: 11, color: "#e74a3b", marginTop: 4, fontWeight: 600 }}>
                                            <i className="fas fa-exclamation-circle mr-1" /> Please select a status
                                        </div>
                                    )}
                                </div>

                                {/* ID Type / Loan Type / Government Number */}
                                <div style={{ display: "grid", gridTemplateColumns: "200px 220px 1fr", gap: "0 20px", alignItems: "start", maxWidth: 820 }}>

                                    {/* ID Type */}
                                    <div>
                                        <label style={{ ...LABEL_STYLE, color: !employeeStatus ? "#b7b9cc" : "#5a5c69" }}>
                                            ID Type *
                                        </label>
                                        <select
                                            value={govType}
                                            onChange={e => handleTypeChange(e.target.value)}
                                            disabled={!employeeStatus}
                                            style={{
                                                ...INPUT_STYLE,
                                                backgroundColor: !employeeStatus ? "#f8f9fc" : "#fff",
                                                color:           !employeeStatus ? "#b7b9cc" : "#3a3b45",
                                                cursor:          !employeeStatus ? "not-allowed" : "pointer",
                                            }}
                                        >
                                            <option value="">— Select ID Type —</option>
                                            <option value="SSS">SSS (Social Security System)</option>
                                            <option value="HDMF">HDMF / Pag-IBIG</option>
                                        </select>
                                        {!employeeStatus && (
                                            <div style={{ fontSize: 11, color: "#b7b9cc", marginTop: 4 }}>
                                                Select employee status first
                                            </div>
                                        )}
                                    </div>

                                    {/* Loan Type */}
                                    <div>
                                        <label style={{ ...LABEL_STYLE, color: !govType ? "#b7b9cc" : "#5a5c69" }}>
                                            Loan Type *
                                        </label>
                                        <select
                                            value={loanType}
                                            onChange={e => handleLoanTypeChange(e.target.value)}
                                            disabled={!govType}
                                            style={{
                                                ...INPUT_STYLE,
                                                backgroundColor: !govType ? "#f8f9fc" : "#fff",
                                                color:           !govType ? "#b7b9cc" : "#3a3b45",
                                                cursor:          !govType ? "not-allowed" : "pointer",
                                            }}
                                        >
                                            <option value="">— Select Loan Type —</option>
                                            <option value="Salary">Salary Loan</option>
                                            <option value="Calamity">Calamity Loan</option>
                                        </select>
                                        {!govType && (
                                            <div style={{ fontSize: 11, color: "#b7b9cc", marginTop: 4 }}>
                                                Select ID type first
                                            </div>
                                        )}
                                    </div>

                                    {/* Government Number */}
                                    <div>
                                        <label style={{ ...LABEL_STYLE, color: !loanType ? "#b7b9cc" : errorFields["governmentNumber"] ? "#e74a3b" : "#5a5c69" }}>
                                            {govType === "HDMF" ? "HDMF Number" : govType === "SSS" ? "SSS Number" : "Government Number"} *
                                        </label>
                                        <div style={{ position: "relative" }}>
                                            <input
                                                value={form.governmentNumber}
                                                onChange={e => handleGovNumberChange(e.target.value)}
                                                placeholder={
                                                    !govType    ? "Select ID type first…"       :
                                                    govType === "SSS"  ? "e.g. 12-3456789-0"   :
                                                    govType === "HDMF" ? "e.g. 1234-5678-9012" :
                                                    !loanType   ? "Select loan type first…"     :
                                                    "Enter government number"
                                                }
                                                disabled={!loanType}
                                                style={{
                                                    ...(errorFields["governmentNumber"] ? INPUT_ERROR_STYLE : INPUT_STYLE),
                                                    borderColor:
                                                        errorFields["governmentNumber"] ? "#e74a3b"  :
                                                        lookupState === "found"         ? "#1cc88a"  :
                                                        lookupState === "error"         ? "#e74a3b"  :
                                                        lookupState === "loading"       ? "#f6c23e"  :
                                                        typeColor ? typeColor.border    : "#d1d3e2",
                                                    backgroundColor: !loanType ? "#f8f9fc" : "#fff",
                                                    color:           !loanType ? "#b7b9cc" : "#3a3b45",
                                                    cursor:          !loanType ? "not-allowed" : "text",
                                                    paddingRight:    36,
                                                }}
                                            />
                                            {/* Lookup status icon */}
                                            {lookupState === "loading" && <i className="fas fa-spinner fa-spin"  style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", color: "#f6c23e", fontSize: 13 }} />}
                                            {lookupState === "found"   && <i className="fas fa-check-circle"    style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", color: "#1cc88a", fontSize: 13 }} />}
                                            {lookupState === "error"   && <i className="fas fa-times-circle"    style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", color: "#e74a3b", fontSize: 13 }} />}
                                        </div>
                                        {!loanType && govType && (
                                            <div style={{ fontSize: 11, color: "#b7b9cc", marginTop: 4 }}>
                                                Select loan type first
                                            </div>
                                        )}
                                    </div>

                                </div>

                                {/* Lookup Status Message */}
                                {lookupMsg && (
                                    <div style={{
                                        marginTop:  10,
                                        fontSize:   12,
                                        fontWeight: 600,
                                        display:    "flex",
                                        alignItems: "center",
                                        gap:        6,
                                        color:
                                            lookupState === "found"   ? "#1cc88a" :
                                            lookupState === "error"   ? "#e74a3b" :
                                            lookupState === "loading" ? "#f6c23e" : "#858796",
                                    }}>
                                        <i className={`fas ${
                                            lookupState === "found"   ? "fa-check-circle"       :
                                            lookupState === "error"   ? "fa-exclamation-circle" :
                                            lookupState === "loading" ? "fa-spinner fa-spin"    : "fa-info-circle"
                                        }`} />
                                        {lookupMsg}
                                    </div>
                                )}

                                {/* No Records Warning */}
                                {noRecordsWarning && (
                                    <div style={{
                                        marginTop:       12,
                                        padding:         "10px 14px",
                                        backgroundColor: "#fff3cd",
                                        border:          "1px solid #ffc107",
                                        borderRadius:    "5px",
                                        fontSize:        "13px",
                                        color:           "#856404",
                                        display:         "flex",
                                        alignItems:      "center",
                                        gap:             8,
                                    }}>
                                        <i className="fas fa-exclamation-triangle"></i>
                                        No <strong>{govType}</strong> <strong>{loanType}</strong> loan payment records found for this employee.
                                        You can still fill in the payment records manually.
                                    </div>
                                )}

                            </div>
                        </div>

                        {/* ── Card 2: Employee Information ── */}
                        <div className="card shadow mb-4">
                            <div className="card-header py-3" style={{ backgroundColor: "#fff", borderBottom: "1px solid #e3e6f0" }}>
                                <h6 className="m-0 font-weight-bold text-primary">
                                    <i className="fas fa-user mr-2"></i>Employee Information
                                </h6>
                            </div>
                            <div className="card-body">
                                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px 20px" }}>

                                    <div>
                                        <label style={{ ...LABEL_STYLE, color: errorFields["employeeName"] ? "#e74a3b" : "#5a5c69" }}>Employee Name *</label>
                                        <input value={form.employeeName} onChange={e => set("employeeName", e.target.value)} placeholder="e.g. Juan Dela Cruz" style={fieldStyle("employeeName")} />
                                    </div>

                                    <div>
                                        <label style={LABEL_STYLE}>{govType === "HDMF" ? "HDMF Number" : "SSS Number"}</label>
                                        <input value={form.governmentNumber} readOnly style={INPUT_READONLY_STYLE} />
                                    </div>

                                    <div style={{ gridColumn: "span 2" }}>
                                        <label style={{ ...LABEL_STYLE, color: errorFields["companyName"] ? "#e74a3b" : "#5a5c69" }}>Company Name *</label>
                                        <input value={form.companyName} onChange={e => set("companyName", e.target.value)} placeholder="Innodata Knowledge Services, Inc." style={fieldStyle("companyName")} />
                                    </div>

                                    <div>
                                        <label style={{ ...LABEL_STYLE, color: errorFields["startDate"] ? "#e74a3b" : "#5a5c69" }}>Employment Start *</label>
                                        <input value={form.startDate} onChange={e => set("startDate", e.target.value)} placeholder="e.g. August 2016" style={fieldStyle("startDate")} />
                                    </div>

                                    <div>
                                        <label style={{ ...LABEL_STYLE, color: employeeStatus === "ACTIVE" ? "#b7b9cc" : errorFields["endDate"] ? "#e74a3b" : "#5a5c69" }}>
                                            Resignation Date {employeeStatus !== "ACTIVE" && "*"}
                                        </label>
                                        <input
                                            value={employeeStatus === "ACTIVE" ? "" : form.endDate}
                                            onChange={e => set("endDate", e.target.value)}
                                            placeholder={employeeStatus === "ACTIVE" ? "Not Applicable" : "e.g. March 26, 2020"}
                                            disabled={employeeStatus === "ACTIVE"}
                                            style={employeeStatus === "ACTIVE" ? INPUT_READONLY_STYLE : fieldStyle("endDate")}
                                        />
                                    </div>

                                    <div>
                                        <label style={{ ...LABEL_STYLE, color: errorFields["paymentStartDate"] ? "#e74a3b" : "#5a5c69" }}>Payment Period Start *</label>
                                        <input value={form.paymentStartDate} onChange={e => handlePaymentPeriodChange("paymentStartDate", e.target.value)} placeholder="e.g. August 2016" style={fieldStyle("paymentStartDate")} />
                                    </div>

                                    <div>
                                        <label style={{ ...LABEL_STYLE, color: errorFields["paymentEndDate"] ? "#e74a3b" : "#5a5c69" }}>Payment Period End *</label>
                                        <input value={form.paymentEndDate} onChange={e => handlePaymentPeriodChange("paymentEndDate", e.target.value)} placeholder="e.g. March 2020" style={fieldStyle("paymentEndDate")} />
                                    </div>

                                </div>
                            </div>
                        </div>

                        {/* ── Card 3: Payment Records ── */}
                        <div className="card shadow mb-4">
                            <div className="card-header py-3" style={{ backgroundColor: "#fff", borderBottom: "1px solid #e3e6f0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <h6 className="m-0 font-weight-bold text-primary">
                                    <i className="fas fa-table mr-2"></i>Payment Records
                                    {govType && (
                                        <span style={{ fontSize: 11, fontWeight: 500, color: "#858796", marginLeft: 8 }}>
                                            {govType === "HDMF" ? "— HDMF LOAN PAYMENTS" : "— SSS (SOCIAL SECURITY SYSTEM) LOAN PAYMENTS"}
                                        </span>
                                    )}
                                </h6>
                                <button onClick={addRow} style={{ background: "#4e73df", color: "#fff", border: "none", borderRadius: "5px", padding: "6px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
                                    <i className="fas fa-plus"></i> Add Row
                                </button>
                            </div>
                            <div className="card-body">

                                {/* Column Headers */}
                                <div style={{ display: "grid", gridTemplateColumns: "2fr 1.4fr 1.2fr 1.4fr 1fr 36px", gap: 6, marginBottom: 8 }}>
                                    {["Month / Year", "SBR No.", "Date Paid", "Loan Type", "Amount", ""].map((h, i) => (
                                        <div key={i} style={{ fontSize: 10, fontWeight: 700, color: "#858796", textTransform: "uppercase", letterSpacing: "0.4px" }}>{h}</div>
                                    ))}
                                </div>

                                {/* Payment Rows */}
                                {payments.map((p, i) => (
                                    <div key={i} style={{ display: "grid", gridTemplateColumns: "2fr 1.4fr 1.2fr 1.4fr 1fr 36px", gap: 6, marginBottom: 8, alignItems: "center" }}>
                                        <input
                                            value={p.month}
                                            onChange={e => setPayment(i, "month", e.target.value)}
                                            placeholder="e.g. SEPTEMBER 2018"
                                            style={!p.month && Object.keys(errorFields).length > 0 ? INPUT_ERROR_STYLE : INPUT_STYLE}
                                        />
                                        <input value={p.sbr}      onChange={e => setPayment(i, "sbr",      e.target.value)} placeholder="UBP38209" style={INPUT_STYLE} />
                                        <input value={p.datePaid} onChange={e => setPayment(i, "datePaid", e.target.value)} placeholder="9-Oct-18" style={INPUT_STYLE} />
                                        <select value={p.loanType} onChange={e => setPayment(i, "loanType", e.target.value)} style={INPUT_STYLE}>
                                            <option value="">— Loan Type —</option>
                                            {LOAN_TYPES.filter(Boolean).map(t => <option key={t} value={t}>{t}</option>)}
                                        </select>
                                        <input value={p.amount} onChange={e => setPayment(i, "amount", e.target.value)} placeholder="1,015.20" style={INPUT_STYLE} />
                                        <button onClick={() => removeRow(i)} title="Remove row" style={{ background: "#e74a3b", color: "#fff", border: "none", borderRadius: "4px", width: 32, height: 34, fontSize: 12, cursor: "pointer" }}>
                                            <i className="fas fa-times" />
                                        </button>
                                    </div>
                                ))}

                                {/* Row Count */}
                                <div style={{ marginTop: 6, fontSize: 11, color: "#b7b9cc" }}>
                                    {payments.length} payment row{payments.length !== 1 ? "s" : ""}
                                    {dbRecords.length > 0 && (
                                        <span style={{ marginLeft: 10, color: "#1cc88a", fontWeight: 600 }}>
                                            <i className="fas fa-database mr-1"></i>
                                            {dbRecords.length} record{dbRecords.length !== 1 ? "s" : ""} loaded
                                        </span>
                                    )}
                                </div>

                            </div>
                        </div>

                        {/* ── Card 4: Issuance Details ── */}
                        <div className="card shadow mb-4">
                            <div className="card-header py-3" style={{ backgroundColor: "#fff", borderBottom: "1px solid #e3e6f0" }}>
                                <h6 className="m-0 font-weight-bold text-primary">
                                    <i className="fas fa-calendar-check mr-2"></i>Issuance Details
                                </h6>
                            </div>
                            <div className="card-body">
                                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 2fr", gap: "14px 20px" }}>

                                    <div>
                                        <label style={{ ...LABEL_STYLE, color: errorFields["issuedDay"] ? "#e74a3b" : "#5a5c69" }}>Day * (e.g. 31st)</label>
                                        <input value={form.issuedDay} onChange={e => set("issuedDay", e.target.value)} placeholder="31st" style={fieldStyle("issuedDay")} />
                                    </div>

                                    <div>
                                        <label style={{ ...LABEL_STYLE, color: errorFields["issuedMonth"] ? "#e74a3b" : "#5a5c69" }}>Month *</label>
                                        <select value={form.issuedMonth} onChange={e => set("issuedMonth", e.target.value)} style={errorFields["issuedMonth"] ? INPUT_ERROR_STYLE : INPUT_STYLE}>
                                            <option value="">Select month</option>
                                            {MONTHS.map(m => <option key={m} value={m}>{m}</option>)}
                                        </select>
                                    </div>

                                    <div>
                                        <label style={{ ...LABEL_STYLE, color: errorFields["issuedYear"] ? "#e74a3b" : "#5a5c69" }}>Year *</label>
                                        <input value={form.issuedYear} onChange={e => set("issuedYear", e.target.value)} placeholder="2024" style={fieldStyle("issuedYear")} />
                                    </div>

                                    <div>
                                        <label style={LABEL_STYLE}>City</label>
                                        <input value={form.issuedCity} onChange={e => set("issuedCity", e.target.value)} placeholder="Legazpi City, Philippines" style={INPUT_STYLE} />
                                    </div>

                                </div>
                            </div>
                        </div>

                        {/* ── Card 5: Generate ── */}
                        <div className="card shadow mb-4">
                            <div className="card-body" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: 16 }}>

                                {/* Info text */}
                                <div style={{ fontSize: 12, color: govType ? "#858796" : "#b7b9cc" }}>
                                    {govType ? (
                                        <>
                                            <i className="fas fa-info-circle mr-1"></i>
                                            Generating <strong>{govType}</strong> certificate
                                            {govType === "SSS" ? " — To: SSS Branch Manager" : " — To: HDMF Branch Manager"}
                                        </>
                                    ) : (
                                        <>
                                            <i className="fas fa-exclamation-circle mr-1"></i>
                                            Select ID type and enter government number to begin
                                        </>
                                    )}
                                </div>

                                {/* Generate Button */}
                                <button
                                    onClick={handleGenerate}
                                    disabled={generating || !govType || !employeeStatus}
                                    style={{
                                        padding:         "10px 28px",
                                        background:      generating || !govType ? "#b7b9cc" : "#4e73df",
                                        color:           "#fff",
                                        border:          "none",
                                        borderRadius:    "6px",
                                        fontSize:        14,
                                        fontWeight:      700,
                                        cursor:          generating || !govType ? "not-allowed" : "pointer",
                                        display:         "flex",
                                        alignItems:      "center",
                                        gap:             8,
                                        boxShadow:       generating || !govType ? "none" : "0 4px 14px rgba(78,115,223,0.35)",
                                        transition:      "all 0.2s",
                                    }}
                                >
                                    <i className={`fas ${generating ? "fa-spinner fa-spin" : "fa-file-word"}`} />
                                    {generating ? "Generating..." : `Generate ${govType || ""} Certificate (.docx)`}
                                </button>

                            </div>
                        </div>

                    </div>
                </div>
                <Footer />
            </div>
        </div>
    );
}